<template>
  <div>
    <!-- Plugin Configuration - what the user needs -->
    <b-card class="mb-3 border-primary">
      <h6><i class="fa fa-plug mr-1" /> Plugin Configuration</h6>
      <p class="text-muted mb-3">Enter these values in your WooCommerce AMS Connector plugin settings.</p>
      <b-row>
        <b-col md="6">
          <b-form-group label="AMS Base URL">
            <b-input-group>
              <b-form-input :value="baseUrl" readonly />
              <b-input-group-append>
                <b-button variant="outline-secondary" size="sm" @click="copyToClipboard(baseUrl)">
                  <i class="fa fa-copy" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Subdomain">
            <b-input-group>
              <b-form-input :value="subdomain" readonly />
              <b-input-group-append>
                <b-button variant="outline-secondary" size="sm" @click="copyToClipboard(subdomain)">
                  <i class="fa fa-copy" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="App Slug">
            <b-input-group>
              <b-form-input :value="connector.slug" readonly />
              <b-input-group-append>
                <b-button variant="outline-secondary" size="sm" @click="copyToClipboard(connector.slug)">
                  <i class="fa fa-copy" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="API Key">
            <b-input-group>
              <b-form-input :value="connector.api_key" readonly />
              <b-input-group-append>
                <b-button variant="outline-secondary" size="sm" @click="copyToClipboard(connector.api_key)">
                  <i class="fa fa-copy" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="API Secret">
            <b-input-group>
              <b-form-input value="Only shown when credentials are generated" readonly disabled />
              <b-input-group-append>
                <b-button variant="outline-warning" size="sm" @click="regenerateCredentials" :disabled="regenerating">
                  <b-spinner v-if="regenerating" small />
                  <i v-else class="fa fa-refresh" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-alert v-if="newCredentials" show variant="warning" class="mb-0">
        <strong>Save this API Secret now — it won't be shown again:</strong>
        <b-input-group class="mt-2">
          <b-form-input :value="newCredentials.api_secret" readonly />
          <b-input-group-append>
            <b-button variant="warning" @click="copyToClipboard(newCredentials.api_secret)">
              <i class="fa fa-copy" /> Copy
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-alert>
    </b-card>

    <b-row>
      <!-- Connection Info -->
      <b-col md="4" class="mb-3">
        <b-card>
          <h6>Connection</h6>
          <div class="mt-2">
            <div><strong>Mode:</strong> {{ connector.connection_mode }}</div>
            <div><strong>Template:</strong> {{ connector.template ? connector.template.name : 'Custom' }}</div>
            <div><strong>Status:</strong>
              <b-badge :variant="connector.is_active ? 'success' : 'secondary'">
                {{ connector.is_active ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- API Stats -->
      <b-col md="4" class="mb-3">
        <b-card>
          <h6>API Usage</h6>
          <div v-if="summary" class="mt-2">
            <div><strong>Last 24h:</strong> {{ (summary.last_24_hours && summary.last_24_hours.total_requests) || 0 }} requests</div>
            <div><strong>Last 7d:</strong> {{ (summary.last_7_days && summary.last_7_days.total_requests) || 0 }} requests</div>
            <div><strong>Total:</strong> {{ ((summary.last_24_hours && summary.last_24_hours.total_requests) || 0) + ((summary.last_7_days && summary.last_7_days.total_requests) || 0) }} requests</div>
          </div>
          <div v-else class="text-center py-2">
            <b-spinner small />
          </div>
        </b-card>
      </b-col>

      <!-- Rate Limits -->
      <b-col md="4" class="mb-3">
        <b-card>
          <h6>Rate Limits</h6>
          <div class="mt-2">
            <div><strong>Per minute:</strong> {{ connector.rate_limit_per_minute || 'N/A' }}</div>
            <div><strong>Per hour:</strong> {{ connector.rate_limit_per_hour || 'N/A' }}</div>
            <div><strong>Per day:</strong> {{ connector.rate_limit_per_day || 'N/A' }}</div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Sync Status + Webhook Stats -->
    <b-row>
      <b-col md="6" class="mb-3">
        <b-card>
          <h6>Sync Status</h6>
          <div v-if="syncStatus">
            <b-table
              :items="syncStatusItems"
              :fields="syncFields"
              small
              borderless
              show-empty
            >
              <template #cell(status)="{ item }">
                <b-badge :variant="item.status === 'synced' ? 'success' : (item.status === 'error' ? 'danger' : 'warning')">
                  {{ item.status }}
                </b-badge>
              </template>
            </b-table>
          </div>
          <div v-else class="text-center py-2">
            <b-spinner small />
          </div>
        </b-card>
      </b-col>

      <b-col md="6" class="mb-3">
        <b-card>
          <h6>Webhook Stats</h6>
          <div v-if="summary">
            <div><strong>Delivered:</strong> {{ (summary.webhook_stats && summary.webhook_stats.total_deliveries_24h) || 0 }}</div>
            <div><strong>Failed:</strong> {{ (summary.webhook_stats && summary.webhook_stats.failed_deliveries_24h) || 0 }}</div>
            <div><strong>Active subscriptions:</strong> {{ (summary.webhook_stats && summary.webhook_stats.active_webhooks) || 0 }}</div>
          </div>
          <div v-else class="text-center py-2">
            <b-spinner small />
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  props: {
    connectorId: { type: Number, required: true },
  },
  data() {
    return {
      regenerating: false,
      newCredentials: null,
      syncFields: [
        { key: 'entity', label: 'Entity' },
        { key: 'last_sync', label: 'Last Sync' },
        { key: 'records', label: 'Records' },
        { key: 'status', label: 'Status' },
      ],
    }
  },
  computed: {
    connector() {
      return this.$store.state.appConnector.item || {}
    },
    summary() {
      return this.$store.state.appConnector.summary
    },
    syncStatus() {
      return this.$store.state.appConnector.syncStatus
    },
    syncStatusItems() {
      if (!this.syncStatus) return []
      const items = Array.isArray(this.syncStatus) ? this.syncStatus : Object.entries(this.syncStatus).map(([entity, data]) => ({ entity, ...data }))
      return items.map(item => ({
        entity: `${item.entity_type || item.entity || ''} (${item.direction || ''})`,
        last_sync: item.last_sync_at ? new Date(item.last_sync_at).toLocaleString() : 'Never',
        records: item.total_synced ?? item.records ?? 0,
        status: item.last_error ? 'error' : (item.last_sync_at ? 'synced' : 'pending'),
      }))
    },
    baseUrl() {
      const origin = window.location.origin
      return origin.replace(/:\d+$/, ':8000')
    },
    subdomain() {
      const params = new URLSearchParams(window.location.search)
      return params.get('subdomain') || window.location.hostname
    },
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('appConnector/fetchSummary', this.connectorId),
      this.$store.dispatch('appConnector/fetchSyncStatus', this.connectorId),
    ])
  },
  methods: {
    async regenerateCredentials() {
      this.regenerating = true
      this.newCredentials = null
      try {
        const data = await this.$store.dispatch('appConnector/regenerateCredentials', this.connectorId)
        this.newCredentials = data
      } finally {
        this.regenerating = false
      }
    },
    copyToClipboard(text) {
      if (text) navigator.clipboard.writeText(text)
    },
  },
}
</script>
